'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  label: string;
  href: string;
  icon?: string;
}

const Header = () => {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const primaryNavItems: NavigationItem[] = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'About', href: '/about', icon: 'UserIcon' },
    { label: 'Experience', href: '/experience-portfolio', icon: 'BriefcaseIcon' },
    { label: 'Education & Credentials', href: '/skills-and-expertise', icon: 'AcademicCapIcon' },
    { label: 'Projects', href: '/projects-and-achievements', icon: 'RocketLaunchIcon' },
  ];

  const secondaryNavItems: NavigationItem[] = [];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActiveRoute = (href: string) => {
    return pathname === href;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-card shadow-elevated' : 'bg-card'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link
            href="/homepage"
            className="flex items-center space-x-3 group"
            onClick={closeMobileMenu}
          >
            <div className="relative">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center transform transition-transform duration-300 group-hover:scale-105">
                <span className="text-white font-heading font-bold text-lg lg:text-xl">
                  CK
                </span>
              </div>
            </div>
            <div className="hidden sm:block">
              <div className="font-heading font-bold text-xl lg:text-2xl text-primary">
                Curtis Kendal
              </div>
              <div className="text-xs text-text-secondary font-mono -mt-1">
                Laboratory Leadership
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                  isActiveRoute(item.href)
                    ? 'bg-primary text-primary-foreground shadow-subtle'
                    : 'text-text-primary hover:bg-muted hover:text-primary'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-3">
            <Link
              href="/admin/login"
              className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-heading font-semibold text-sm hover:bg-primary/90 transition-all duration-300 hover:shadow-subtle transform hover:-translate-y-0.5 flex items-center space-x-2"
            >
              <Icon name="LockClosedIcon" size={18} />
              <span>Login</span>
            </Link>
            <a
              href="mailto:kendal@laboratory-leadership.com"
              className="px-5 py-2.5 bg-accent text-accent-foreground rounded-lg font-heading font-semibold text-sm hover:bg-accent/90 transition-all duration-300 hover:shadow-subtle transform hover:-translate-y-0.5"
            >
              Contact Me
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-text-primary hover:bg-muted transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-card z-40">
          <nav className="flex flex-col h-full overflow-y-auto">
            <div className="flex-1 px-4 py-6 space-y-1">
              {[...primaryNavItems, ...secondaryNavItems].map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-4 rounded-lg font-medium transition-all duration-300 ${
                    isActiveRoute(item.href)
                      ? 'bg-primary text-primary-foreground shadow-subtle'
                      : 'text-text-primary hover:bg-muted hover:text-primary'
                  }`}
                >
                  {item.icon && <Icon name={item.icon as any} size={24} />}
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-border p-4 space-y-3">
              <Link
                href="/admin/login"
                className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-all duration-300"
                onClick={closeMobileMenu}
              >
                <Icon name="LockClosedIcon" size={20} />
                <span>Login</span>
              </Link>
              <a
                href="mailto:kendal@laboratory-leadership.com"
                className="flex items-center justify-center space-x-2 w-full px-5 py-3 bg-accent text-accent-foreground rounded-lg font-heading font-semibold hover:bg-accent/90 transition-all duration-300"
                onClick={closeMobileMenu}
              >
                <Icon name="EnvelopeIcon" size={20} />
                <span>Contact Me</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;